<template>
  <component
    :is="component"
    type="button"
    :class="[
      variant,
      'direction-' + direction,
      'aspect-' + aspect,
      `elevate-${elevate}`,
      theme,
      'stroke-' + stroke,
      { disabled: disabled },
      { 'limit-lines': nbLines > -1 },
      { 'icon-scale-to-fit': iconScaleToFit },
      { selected: selected },
    ]"
    class="button-card"
    v-bind="props"
    @click="handleBaseClick"
  >
    <span v-if="$slots.icon" class="icon"><slot name="icon" /></span>
    <span v-if="$slots.default" class="content"><slot /></span>
    <div v-if="cornerBannerText" class="banner-text">
      <span>{{ cornerBannerText }}</span>
    </div>
  </component>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router';
import type { NuxtLinkProps } from '#app';
export interface ButtonCardProps extends NuxtLinkProps {
  theme?: string;
  stroke?: string;
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  elevate?: 'none' | 'all' | 'desktop' | 'mobile';
  aspect?:
    | 'default'
    | 'square'
    | 'vertical'
    | '4-3'
    | '14-9'
    | '16-9'
    | 'pano'
    | '14-13';
  variant?: 'tonal' | 'outlined';
  disabled?: boolean;
  to?: string | RouteLocationRaw;
  cornerBannerText?: string | null;
  cornerBannerColor?: string | null;
  nbLines?: number;
  iconScaleToFit?: boolean;
  selected?: boolean;
}
const props = defineProps({
  theme: { default: 'primary' },
  stroke: { default: '0' },
  direction: { default: 'column' },
  elevate: null,
  aspect: null,
  variant: { default: 'outlined' },
  disabled: { type: Boolean },
  to: null,
  cornerBannerText: { default: null },
  cornerBannerColor: { default: null },
  nbLines: { default: 3 },
  iconScaleToFit: { type: Boolean, default: false },
  selected: { type: Boolean, default: false }
});

const component = computed(() => {
  if (props.to || props.href) {
    return resolveComponent('NuxtLink');
  }
  return 'button';
});

const activeElement = useActiveElement();

const bannercolor = computed(() => props.cornerBannerColor);

const handleBaseClick = () => {
  if (activeElement.value) activeElement.value.blur();
};
</script>
<style lang="scss" scoped>
.button-card {
  @include font('xs', 300, 'secondary');

  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;
  min-height: 4.6rem;
  overflow: hidden;
  perspective: 600px;
  perspective-origin: top right;
  transition: color 300ms $ease-out-quad 200ms,
    background-color 300ms $ease-out-quad 200ms, border 500ms $ease-out-quad 0ms;
  font-family: var(--font_family_secondary);
  font-weight: var(--font_weight_600);
  text-align: center;
  row-gap: var(--gap_n);
  &.aspect-vertical {
    row-gap: var(--gap_xl);
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: transform 0ms $ease-in-out-quad 300ms;
  }

  &::after {
    transition: transform 300ms $ease-in-out-quad;
  }

  .icon,
  .content {
    z-index: 1;
  }
  .content {
    padding: 0 1rem;
  }
  &.limit-lines {
    .content {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      -webkit-line-clamp: v-bind('nbLines');
      -webkit-box-orient: vertical;
    }
  }

  &.icon-scale-to-fit {
    row-gap: 0;
    align-items: space-between;

    .content {
      display: flex;
      flex-basis: 56%;
      align-items: center;
      align-self: center;
      justify-content: center;
    }
    .icon {
      flex-basis: 44%;
      align-self: center;
      overflow: hidden;
      :deep(.image) {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  &:hover,
  &:focus,
  &.active,
  &.disabled,
  &.selected,
  &.router-link-exact-active {
    &::before {
      transform: scale(1) translateX(-101%);
      transition: transform 500ms $ease-in-out-quad;
      opacity: 1;
    }

    &::after {
      transform: translateX(101%);
      transition: transform 0ms;
    }
  }

  &.active,
  &.disabled,
  &.router-link-exact-active {
    cursor: auto;
  }

  .banner-text {
    display: flex;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0.2rem 0;
    transform: translate(-60%, -60%) rotate(-45deg);
    background-color: v-bind(bannercolor);
    box-shadow: rgb(67 71 85 / 27%) 0 0 0.25em,
      rgb(90 125 188 / 5%) 0 0.25em 1em;
    text-align: center;
    span {
      display: inline-block;
      width: 60%;
      padding: 0;
      overflow: hidden;
      color: var(--color_neutral_50);
      font-size: var(--text_size_xxs);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &.elevate-all {
    box-shadow: rgb(0 0 0 / 10%) 0 1px 3px 0, rgb(0 0 0 / 6%) 0 1px 2px 0;
  }
  &.elevate-desktop {
    @include desktop {
      box-shadow: rgb(0 0 0 / 10%) 0 1px 3px 0, rgb(0 0 0 / 6%) 0 1px 2px 0;
    }
  }
}
</style>
